import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Camera,
  Check,
  CheckCircle2,
  Copy,
  QrCode,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Wifi,
} from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";

const FRONTEND_URL = window.location.origin;
const CAMERA_SLOTS = [1, 2, 3, 4, 5];

const ProModeQRCodes = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [session, setSession] = useState(null);
  const [connectionToken, setConnectionToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedDevice, setCopiedDevice] = useState(null);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const eventRes = await axiosInstance.get(`/events/${eventId}`);
        if (cancelled) return;
        setEvent(eventRes.data);

        let sessionData;
        try {
          const sessionRes = await axiosInstance.get(`/pro-mode/session/${eventId}`);
          sessionData = sessionRes.data;
        } catch {
          const createRes = await axiosInstance.post(`/pro-mode/session/create?event_id=${eventId}`);
          sessionData = createRes.data;
        }

        if (cancelled) return;
        setSession(sessionData);
        setConnectionToken(sessionData.connection_token);
        setLastCheckedAt(new Date());
      } catch (error) {
        if (!cancelled) toast.error("Failed to load camera connections");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [eventId]);

  // Registration happens on the phone before it joins LiveKit, so a registered
  // slot is safe to show as connected while the Control Panel confirms the
  // live participant directly from the LiveKit room.
  useEffect(() => {
    if (!connectionToken) return undefined;
    let cancelled = false;
    let timeoutId;

    const pollConnections = async () => {
      try {
        const response = await axiosInstance.get(`/pro-mode/session/${eventId}`);
        if (!cancelled) {
          setSession(response.data);
          setLastCheckedAt(new Date());
        }
      } catch {
        // Preserve the last known state during a brief network interruption.
      } finally {
        if (!cancelled) timeoutId = window.setTimeout(pollConnections, 2000);
      }
    };

    pollConnections();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [connectionToken, eventId]);

  const connectedDeviceNumbers = useMemo(() => new Set(
    (session?.devices || [])
      .filter((device) => device.is_connected)
      .map((device) => Number(device.device_number)),
  ), [session?.devices]);

  const getDeviceUrl = (deviceNumber) =>
    `${FRONTEND_URL}/pro-mode/camera/${eventId}/${deviceNumber}?token=${connectionToken}`;

  const handleCopyLink = async (deviceNumber) => {
    try {
      await navigator.clipboard.writeText(getDeviceUrl(deviceNumber));
      setCopiedDevice(deviceNumber);
      toast.success(`Camera ${deviceNumber} link copied`);
      window.setTimeout(() => setCopiedDevice(null), 2000);
    } catch {
      toast.error("Unable to copy the camera link");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080c16] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 mx-auto mb-3 animate-spin" />
          <p className="text-white font-semibold">Preparing camera connections...</p>
        </div>
      </div>
    );
  }

  const connectedCount = connectedDeviceNumbers.size;

  return (
    <div className="min-h-screen bg-[#080c16] text-white" data-testid="pro-mode-qr-page">
      <header className="sticky top-0 z-30 h-[62px] border-b border-white/[0.07] bg-[#0b1020]/95 px-4 backdrop-blur-xl">
        <div className="mx-auto flex h-full max-w-[1440px] items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/pro-mode/${eventId}`)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-gray-400 transition hover:border-white/20 hover:text-white"
              aria-label="Back to Control Panel"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-base font-extrabold">QR Camera Connections</h1>
                <span className="rounded-md bg-blue-500/20 px-2 py-1 text-[9px] font-extrabold tracking-wider text-blue-300">PRO MODE</span>
              </div>
              <p className="truncate text-[10px] text-gray-500">{event?.title}</p>
            </div>
          </div>

          <div className={`flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-xs font-bold ${
            connectedCount > 0
              ? "border-green-400/30 bg-green-400/10 text-green-300"
              : "border-white/10 bg-white/[0.04] text-gray-300"
          }`}>
            <span className={`h-2 w-2 rounded-full ${connectedCount > 0 ? "bg-green-400 shadow-[0_0_10px_rgba(74,222,128,.8)]" : "bg-gray-600"}`} />
            {connectedCount} of 5 connected
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-5 py-4">
        <section className="relative mb-4 overflow-hidden rounded-2xl border border-blue-400/25 bg-[#0d1b36] px-5 py-4">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,.14),transparent_44%)]" />
          <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 max-w-2xl">
              <div className="mb-1 flex items-center gap-2 text-blue-400">
                <Smartphone className="h-4 w-4" />
                <span className="text-[10px] font-extrabold uppercase tracking-[0.18em]">Connect your cameras</span>
              </div>
              <h2 className="text-xl font-black tracking-tight">Scan once. Allow both. You're connected.</h2>
              <p className="mt-1 text-xs leading-5 text-gray-400">Camera and microphone access are requested together. Each slot flips to <strong className="text-green-300">Connected</strong> after that phone is ready.</p>
            </div>

            <div className="grid shrink-0 grid-cols-3 gap-2 text-center">
              {[
                ["1", "Scan code"],
                ["2", "Allow camera + mic"],
                ["3", "See connected"],
              ].map(([step, label]) => (
                <div key={step} className="w-28 rounded-xl border border-white/[0.08] bg-white/[0.035] px-2 py-3">
                  <div className="mx-auto mb-1.5 grid h-6 w-6 place-items-center rounded-full bg-blue-500/20 text-[11px] font-black text-blue-300">{step}</div>
                  <p className="text-[10px] font-semibold text-gray-300">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="mb-3 flex items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-extrabold">Camera slots</h2>
            <p className="mt-0.5 text-[11px] text-gray-500">Connect up to five phones or tablets.</p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-500">
            <RefreshCw className="h-3.5 w-3.5" />
            Live status {"\u00b7"} {lastCheckedAt ? lastCheckedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" }) : "starting"}
          </div>
        </div>

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5" aria-label="Camera QR codes">
          {connectionToken && CAMERA_SLOTS.map((deviceNumber) => {
            const isConnected = connectedDeviceNumbers.has(deviceNumber);
            return (
              <article
                key={deviceNumber}
                data-testid={`qr-camera-${deviceNumber}`}
                data-connected={isConnected ? "true" : "false"}
                className={`flex min-h-[390px] flex-col overflow-hidden rounded-2xl border bg-[#0d1424] p-3 transition duration-300 ${
                  isConnected
                    ? "border-green-400/40 shadow-[0_0_28px_rgba(34,197,94,.08)]"
                    : "border-white/[0.08] hover:-translate-y-0.5 hover:border-blue-400/35"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${isConnected ? "bg-green-400/15 text-green-300" : "bg-blue-500/15 text-blue-300"}`}>
                    <Camera className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold">Camera {deviceNumber}</h3>
                    <p className="text-[10px] text-gray-500">Slot {deviceNumber} of 5</p>
                  </div>
                </div>

                <div className={`mx-auto mt-3 flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-bold ${
                  isConnected
                    ? "border-green-400/30 bg-green-400/10 text-green-300"
                    : "border-yellow-400/35 bg-yellow-400/[0.08] text-yellow-300"
                }`}>
                  {isConnected ? <CheckCircle2 className="h-3 w-3" /> : <Wifi className="h-3 w-3" />}
                  {isConnected ? "Connected" : "Waiting"}
                </div>

                <div className="flex flex-1 flex-col items-center justify-center py-3">
                  <div className="relative rounded-xl bg-white p-2.5 shadow-[0_16px_42px_rgba(0,0,0,.35)]">
                    <QRCode value={getDeviceUrl(deviceNumber)} size={132} level="H" />
                    {isConnected && (
                      <div className="absolute inset-0 grid place-items-center rounded-xl bg-[#07110a]/88 backdrop-blur-[2px]">
                        <div className="text-center">
                          <div className="mx-auto mb-2 grid h-11 w-11 place-items-center rounded-full bg-green-400 text-[#07110a] shadow-[0_0_24px_rgba(74,222,128,.5)]">
                            <Check className="h-6 w-6 stroke-[3]" />
                          </div>
                          <p className="text-xs font-black text-green-300">Connected</p>
                        </div>
                      </div>
                    )}
                  </div>

                </div>

                <p className="mb-2 text-center text-[10px] leading-4 text-gray-400">
                  {isConnected ? "This camera is ready in the Control Panel." : "Scan with the phone for this slot."}
                </p>

                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-full border-white/10 bg-white/[0.03] text-[11px] text-gray-200 hover:bg-white/[0.08] hover:text-white"
                  onClick={() => handleCopyLink(deviceNumber)}
                >
                  {copiedDevice === deviceNumber ? (
                    <><Check className="mr-2 h-3.5 w-3.5 text-green-400" />Copied</>
                  ) : (
                    <><Copy className="mr-2 h-3.5 w-3.5" />Copy camera link</>
                  )}
                </Button>
              </article>
            );
          })}
        </section>

        <section className="mt-3 flex items-center justify-between gap-4 rounded-2xl border border-white/[0.08] bg-[#0d1424] px-5 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <ShieldCheck className="h-5 w-5 shrink-0 text-blue-400" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-200">Secure event-only camera links</p>
              <p className="truncate text-[10px] text-gray-500">Only share these links with your camera operators. Each link is tied to this Pro Mode event.</p>
            </div>
          </div>
          <Button
            type="button"
            onClick={() => navigate(`/pro-mode/${eventId}`)}
            className="h-11 shrink-0 bg-gradient-to-r from-blue-500 to-blue-700 px-6 text-sm font-bold shadow-[0_8px_24px_rgba(37,99,235,.28)] hover:from-blue-400 hover:to-blue-600"
          >
            <QrCode className="mr-2 h-4 w-4" />Back to Control Panel
          </Button>
        </section>
      </main>
    </div>
  );
};

export default ProModeQRCodes;
